import {
  isBankCell,
  isBond,
  isBrokerageAccount,
  isCentralizedWallet,
  isCommercialRealEstate,
  isCommodityAssetToken,
  isDebitAccount,
  isJewelry,
  isLand,
  isLegalEntity,
  isMetalAccount,
  isMyFund,
  isPreciousGemstones,
  isPublicFund,
  isResidentialRealEstate,
  isSecuritiesAndStocks,
} from '@/entities/assets/helpers/guards';
import { AiAsset, ApiExtractItem } from '@/shared/api';
import { ContactDto } from '@/shared/lib/sj-orm';
import { ISjCollection } from '@/shared/lib/sj-orm/core/types';
import { generateDtoId } from '@/shared/lib/sj-orm/helpers';
import { AssetDto, isAssetCategory } from '@/shared/lib/sj-orm/models/asset.dto';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import {
  isPersonalIdentifierType,
  PersonalIdentifierDocumentDto,
} from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { CollectionName, CollectionToDtoMap } from '@/shared/lib/sj-orm/types';
import { AIStatus } from '@/shared/types';

import { EntitiesList } from '../types';

import { makeContacts, makePrivateDoc } from './builders';
import { getValidAsset, getValidPersonalId } from './validator';

export const makeEntities = (
  items: ApiExtractItem[],
  idFilePathMap: Record<string, string>,
): EntitiesList => {
  const result: EntitiesList = {
    assets: [],
    privateDocuments: [],
    personalIdentifiers: [],
    contacts: [],
  };

  const aiProps = {
    aiStatus: AIStatus.UNVERIFIED,
  };

  for (const item of items) {
    switch (item.kind) {
      case 'asset': {
        const privateDocument = item.personal_documents?.private_document;
        const assetData = getValidAsset({ data: item.data as AiAsset, type: item.asset_type });

        if (!assetData || !isAssetCategory(assetData.category)) break;
        let linkedPrivateDocumentId: string | null = null;

        if (privateDocument) {
          const newDocument = makePrivateDoc(aiProps, privateDocument);

          if (newDocument) {
            linkedPrivateDocumentId = newDocument.id;
            result.privateDocuments.push(newDocument);
          }
        }

        if (item.contacts?.length) {
          const filteredContacts = makeContacts(aiProps, item.contacts);
          result.contacts.push(...filteredContacts);
        }

        const asset: AssetDto = {
          id: generateDtoId(CollectionName.ASSETS),
          ...assetData,
          ...(linkedPrivateDocumentId ? { otherDocuments: [linkedPrivateDocumentId] } : {}),
          ...aiProps,
          attachments: item.file_id ? [idFilePathMap[item.file_id]] : undefined,
        };

        result.assets.push(asset);
        break;
      }

      case 'id_document': {
        if (!item.id_type) break;
        const privateDocument = item.personal_documents?.private_document;

        if (privateDocument) {
          const newDocument = makePrivateDoc(aiProps, privateDocument);

          if (newDocument) result.privateDocuments.push(newDocument);
        }

        if (item.contacts?.length) {
          const filteredContacts = makeContacts(aiProps, item.contacts);
          result.contacts.push(...filteredContacts);
        }

        if (!isPersonalIdentifierType(item.id_type)) break;
        const personalIdData = getValidPersonalId({ data: item.data, type: item.id_type });
        if (!personalIdData) break;

        const pid: PersonalIdentifierDocumentDto = {
          id: generateDtoId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS),
          ...personalIdData,
          type: DocumentType.PERSONAL_IDENTIFIERS,
          personalIdentifierType: item.id_type,
          ...aiProps,
          attachments: item.file_id ? [idFilePathMap[item.file_id]] : undefined,
        };

        result.personalIdentifiers.push(pid);
        break;
      }

      case 'personal_document': {
        const privateDocument = item.personal_documents?.private_document;

        if (privateDocument) {
          const newDocument = makePrivateDoc(aiProps, privateDocument);

          if (newDocument) result.privateDocuments.push(newDocument);
        }
        break;
      }

      default:
        break;
    }
  }

  return result;
};

export const getAssetIdentityValue = (asset: AssetDto) => {
  if (isDebitAccount(asset)) {
    return asset.cif;
  }

  if (isResidentialRealEstate(asset)) {
    return asset.residentialRealEstateNumber;
  }

  if (isCommercialRealEstate(asset)) {
    return asset.commercialRealEstateNumber;
  }

  if (isLand(asset)) {
    return asset.number;
  }

  if (isLegalEntity(asset)) {
    return asset.taxNumber;
  }

  if (isJewelry(asset)) {
    return asset.serialNumber;
  }

  if (isBankCell(asset)) {
    return asset.depositBoxNumber;
  }

  if (isMetalAccount(asset)) {
    return asset.iban;
  }

  if (isCentralizedWallet(asset)) {
    return asset.accountNumber;
  }

  if (isPreciousGemstones(asset)) {
    return asset.serialNumber;
  }

  if (isCommodityAssetToken(asset)) {
    return asset.identifierNumber;
  }

  if (isBrokerageAccount(asset)) {
    return asset.cif;
  }

  if (isSecuritiesAndStocks(asset)) {
    return asset.isin;
  }

  if (isBond(asset)) {
    return asset.isin;
  }

  if (isMyFund(asset)) {
    return asset.taxNumber;
  }

  if (isPublicFund(asset)) {
    return asset.taxNumber;
  }

  return null;
};

export const shouldMatchPersonalId = (
  a: PersonalIdentifierDocumentDto,
  b: PersonalIdentifierDocumentDto,
) => {
  if (!a.number || !b.number || a.type !== b.type) return false;
  return a.number === b.number;
};

export const shouldMatchContact = (a: ContactDto, b: ContactDto) => {
  const isEmailMatched = Boolean(a.email) && Boolean(b.email) && a.email === b.email;
  const isPhoneMatched =
    Boolean(a.phone) &&
    Boolean(b.phone) &&
    a.phone?.Dropdown === b.phone?.Dropdown &&
    a.phone?.Input === b.phone?.Input;

  return a.type === b.type && (isEmailMatched || isPhoneMatched);
};

export const createOrUpdateEntity = <
  TColName extends CollectionName,
  T extends CollectionToDtoMap[TColName],
>({
  collection,
  newData,
  existingItems,
  publicKey,
}: {
  collection: ISjCollection<TColName, T>;
  newData: T;
  existingItems: T[] | undefined;
  publicKey: string;
}) => {
  if (!collection) return;

  if (!existingItems || existingItems.length === 0) {
    collection.create(newData, [publicKey]);
  } else {
    existingItems.forEach((existingItem) => {
      collection.update({
        ...existingItem,
        ...newData,
        id: existingItem.id,
      });
    });
  }
};
